import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import urlBuilder from 'garaje/utils/url-builder';

interface AdminApprovalItem {
  id: string;
  employee_name: string;
  employee_email: string;
  grace_ends_at: string;
  steps?: Array<{ mode: string }>;
}

interface AdminApprovalsModel {
  pending: AdminApprovalItem[];
  bulkPaused: AdminApprovalItem[];
  failed: AdminApprovalItem[];
  approvals: AdminApprovalItem[];
  meta: { total: number };
}

interface AjaxFetchService {
  request(url: string, options?: { method?: string; data?: unknown }): Promise<unknown>;
}

export default class LocationOverviewHrisAdminApprovalsController extends Controller {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;

  declare model: AdminApprovalsModel;

  queryParams = ['page', 'status'];

  statusOptions = ['All', 'pending', 'approved', 'denied'];

  @tracked page = 1;
  @tracked status: string | undefined;

  @tracked testPhase: 'form' | 'running' | 'done' = 'form';
  @tracked testEmployeeQuery = '';

  @tracked selectedPending: Record<string, boolean> = {};
  @tracked selectedBulk: Record<string, boolean> = {};

  @tracked isSubmitting = false;
  @tracked submitError: string | null = null;

  get isGlobal(): boolean {
    return this.abilities.can('manage all hris-dashboard');
  }

  get isReadOnly(): boolean {
    return this.abilities.cannot('manage all hris-dashboard') && this.abilities.cannot('manage hris-dashboard');
  }

  get pending(): AdminApprovalItem[] {
    return this.model?.pending ?? [];
  }

  get bulkPaused(): AdminApprovalItem[] {
    return this.model?.bulkPaused ?? [];
  }

  get failed(): AdminApprovalItem[] {
    return this.model?.failed ?? [];
  }

  get failedLive(): AdminApprovalItem[] {
    return this.failed.filter((item) => item.steps?.some((s) => s.mode === 'live'));
  }

  get approvals(): AdminApprovalItem[] {
    return this.model?.approvals ?? [];
  }

  get meta(): { total: number } {
    return this.model?.meta ?? { total: 0 };
  }

  get pageCount(): number {
    return Math.ceil(this.meta.total / 25);
  }

  get selectedPendingIds(): string[] {
    return Object.keys(this.selectedPending).filter((k) => this.selectedPending[k]);
  }

  get allPendingSelected(): boolean {
    const ids = this.pending.map((i) => i.id);
    return ids.length > 0 && ids.every((id) => this.selectedPending[id]);
  }

  get selectedBulkIds(): string[] {
    return Object.keys(this.selectedBulk).filter((k) => this.selectedBulk[k]);
  }

  get allBulkSelected(): boolean {
    const ids = this.bulkPaused.map((i) => i.id);
    return ids.length > 0 && ids.every((id) => this.selectedBulk[id]);
  }

  // Wraps every mutation: sets loading state, catches errors, refreshes route on success.
  private async mutate(fn: () => Promise<unknown>): Promise<void> {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = null;
    try {
      await fn();
      // Reload the model so the acted-on routine drops out of the pending list.
      // Refresh the currently-active leaf route by name — this controller is shared
      // by both the global (/hris-dashboard) and location-overview mount points.
      await this.router.refresh(this.router.currentRouteName);
    } catch (err) {
      this.submitError = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      // eslint-disable-next-line no-console
      console.error('[hris-dashboard][admin-approvals][mutate] request failed:', err);
    } finally {
      this.isSubmitting = false;
    }
  }

  @action togglePendingItem(id: string): void {
    if (!this.isGlobal) return;
    this.selectedPending = { ...this.selectedPending, [id]: !this.selectedPending[id] };
  }

  @action toggleAllPending(): void {
    if (this.allPendingSelected) {
      this.selectedPending = {};
    } else {
      const next: Record<string, boolean> = {};
      this.pending.forEach((i) => (next[i.id] = true));
      this.selectedPending = next;
    }
  }

  @action async runNowSelected(): Promise<void> {
    if (!this.isGlobal) return;
    const ids = this.selectedPendingIds;
    await this.mutate(async () => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${id}/expedite`), { method: 'POST' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) throw new Error(`${failed} of ${ids.length} expedite requests failed`);
      this.selectedPending = {};
    });
  }

  @action async cancelSelected(): Promise<void> {
    if (!this.isGlobal) return;
    const ids = this.selectedPendingIds;
    await this.mutate(async () => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${id}/cancel`), { method: 'POST' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) throw new Error(`${failed} of ${ids.length} cancel requests failed`);
      this.selectedPending = {};
    });
  }

  @action setPage(page: number): void {
    this.page = page;
  }

  @action setStatus(value: string | undefined): void {
    this.status = value === 'All' ? undefined : value;
    this.page = 1;
  }

  @action toggleBulkItem(id: string): void {
    this.selectedBulk = { ...this.selectedBulk, [id]: !this.selectedBulk[id] };
  }

  @action toggleAllBulk(): void {
    if (this.allBulkSelected) {
      this.selectedBulk = {};
    } else {
      const next: Record<string, boolean> = {};
      this.bulkPaused.forEach((i) => (next[i.id] = true));
      this.selectedBulk = next;
    }
  }

  @action async approve(approvalId: string): Promise<void> {
    await this.mutate(() =>
      this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${approvalId}/approve`), {
        method: 'POST',
      }),
    );
  }

  @action async deny(approvalId: string): Promise<void> {
    await this.mutate(() =>
      this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${approvalId}/deny`), { method: 'POST' }),
    );
  }

  @action async runNow(approvalId: string): Promise<void> {
    await this.mutate(() =>
      this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${approvalId}/expedite`), {
        method: 'POST',
      }),
    );
  }

  @action async cancelApproval(approvalId: string): Promise<void> {
    await this.mutate(() =>
      this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${approvalId}/cancel`), { method: 'POST' }),
    );
  }

  @action async approveBulk(id: string): Promise<void> {
    await this.mutate(() =>
      this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${id}/approve`), { method: 'POST' }),
    );
  }

  @action async approveBulkSelected(): Promise<void> {
    const ids = this.selectedBulkIds;
    await this.mutate(async () => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${id}/approve`), { method: 'POST' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) throw new Error(`${failed} of ${ids.length} approve requests failed`);
      this.selectedBulk = {};
    });
  }

  @action async cancelBulkSelected(): Promise<void> {
    const ids = this.selectedBulkIds;
    await this.mutate(async () => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          this.ajaxFetch.request(urlBuilder.hrisLifecycle.url(`/admin-approvals/${id}/cancel`), { method: 'POST' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) throw new Error(`${failed} of ${ids.length} cancel requests failed`);
      this.selectedBulk = {};
    });
  }

  @action async retryFailed(id: string): Promise<void> {
    await this.mutate(() => this.ajaxFetch.request(`/api/v1/hris/failed-actions/${id}/retry`, { method: 'POST' }));
  }

  @action async dismissFailed(id: string): Promise<void> {
    await this.mutate(() => this.ajaxFetch.request(`/api/v1/hris/failed-actions/${id}/dismiss`, { method: 'POST' }));
  }

  @action startDryRun(): void {
    this.testPhase = 'running';
    setTimeout(() => {
      this.testPhase = 'done';
    }, 2600);
  }

  @action resetTest(): void {
    this.testPhase = 'form';
    this.testEmployeeQuery = '';
  }
}
